import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { formatBookPageLabel } from '@/utils/bookPageLabel';
import type { DisplayMode } from '@/types/book';
import { useProjectStore } from './project';

export const useReaderControlsStore = defineStore('readerControls', () => {
  const projectStore = useProjectStore();

  const currentPageIndex = ref(0);
  const displayMode = ref<DisplayMode>('double');
  const isPlaying = ref(false);
  const playbackProgress = ref(0);
  const isZoomed = ref(false);
  const isFullscreen = ref(false);
  const isBgmPlaying = ref(false);
  const isBgmReady = ref(false);

  const totalPages = computed(() => projectStore.pages.length);
  const currentPageLabel = computed(() =>
    formatBookPageLabel(currentPageIndex.value, totalPages.value, displayMode.value)
  );
  const isFirstPage = computed(() => currentPageIndex.value <= 0);
  const isLastPage = computed(() => {
    if (totalPages.value === 0) return true;
    if (displayMode.value === 'double') {
      return currentPageIndex.value + 2 >= totalPages.value;
    }
    return currentPageIndex.value >= totalPages.value - 1;
  });
  /** 首页禁用上一页、末页禁用下一页；播放中两侧均禁用。 */
  const canGoPrev = computed(() => !isFirstPage.value && !isPlaying.value);
  const canGoNext = computed(() => !isLastPage.value && !isPlaying.value);

  const muted = computed(() => projectStore.muted);
  const volume = computed(() => projectStore.volume);
  const effectiveVolume = computed(() =>
    muted.value ? 0 : Math.max(0, Math.min(1, volume.value))
  );
  const hasBgm = computed(() => Boolean(projectStore.bgm));
  const canToggleBgm = computed(() => hasBgm.value && isBgmReady.value);

  function reset() {
    currentPageIndex.value = 0;
    displayMode.value = 'double';
    isPlaying.value = false;
    playbackProgress.value = 0;
    isZoomed.value = false;
    isFullscreen.value = false;
    isBgmPlaying.value = false;
    isBgmReady.value = false;
  }

  return {
    currentPageIndex,
    displayMode,
    totalPages,
    currentPageLabel,
    isFirstPage,
    isLastPage,
    canGoPrev,
    canGoNext,
    isPlaying,
    playbackProgress,
    isZoomed,
    isFullscreen,
    muted,
    volume,
    effectiveVolume,
    hasBgm,
    isBgmPlaying,
    isBgmReady,
    canToggleBgm,
    reset
  };
});
